import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClubPanel, PanelHead, PillButton, TagPill, EmptyBlock, useArmed, EVENT_STATUS_PILL, MUTED, SUBTLE, RULE, RED } from './clubUi';
import ClubEventRow from './ClubEventRow';
import LoadFailed from './LoadFailed';
import eventService from '../../services/eventService';
import { whenLabel, dayLabel, dateLabel, plural, money } from '../../utils/surfaceFormat';
import { posterPlaceholder } from '../../utils/posterPlaceholder';

/**
 * EVENTS (README §1): GATHERINGS (rows with status pill, SEE IT / FINISH IT for drafts; admin
 * `+ NEW GATHERING` cyan) · the admin tools row LINK EXISTING (opens the grey LINK ONE OF YOUR
 * EVENTS panel with LINK per event) · CREATE EVENT · SHOW HIDDEN (cyan when on) · UPCOMING · N
 * (per-row HIDE/UNHIDE, UNLINK red + armed; hidden rows 55% + `· HIDDEN FROM MEMBERS`) · PAST · N.
 * A paid visitor sees public events only.
 */
export const UPCOMING_STATUSES = ['draft', 'selling', 'funding', 'processing_greenlight', 'greenlit'];

export function splitEvents(events = [], now = new Date()) {
  const upcoming = events.filter((e) => new Date(e.dateTime).getTime() > now.getTime() || UPCOMING_STATUSES.includes(e.status));
  const past = events.filter((e) => !upcoming.includes(e));
  return { upcoming, past };
}

export function statusPill(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'canceled' || s === 'cancelled' || s === 'dissolved') return { label: s === 'dissolved' ? 'DISSOLVED' : 'CANCELLED', tone: 'red' };
  if (s === 'ended' || s === 'completed') return { label: 'ENDED', tone: 'hairline' };
  if (s === 'draft') return { label: 'DRAFT', tone: 'outline' };
  if (s === 'funding') return { label: 'FUNDING', tone: 'magenta' };
  if (s === 'greenlit' || s === 'processing_greenlight') return { label: 'GREENLIT', tone: 'glitchGreen' };
  if (s === 'selling') return { label: 'SELLING', tone: 'black' };
  return { label: s.replace(/_/g, ' ').toUpperCase() || '—', tone: 'outline' };
}

export { ClubEventRow as EventRow };

function LinkPicker({ linkedIds, onLink, busy, error, phone }) {
  const [mine, setMine] = useState(null);
  useEffect(() => {
    let done = false;
    eventService.getEvents().then((r) => {
      if (done) return;
      setMine(r.success ? (r.data || []).filter((e) => !linkedIds.has(e.id)) : []);
    });
    return () => { done = true; };
  }, [linkedIds]);
  return (
    <ClubPanel phone={phone} bg="#F2F2F2" data-testid="link-picker">
      <PanelHead title="LINK ONE OF YOUR EVENTS" right={busy ? 'LINKING…' : undefined} />
      {mine === null ? (
        <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>LOADING YOUR EVENTS…</span>
      ) : mine.length === 0 ? (
        <span style={{ fontSize: 12, color: MUTED }}>Nothing of yours to link. Create an event and it lands here.</span>
      ) : (
        <div className="flex flex-col">
          {mine.map((ev, i) => (
            <div key={ev.id} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '12px 0', borderTop: i ? '2px solid #E5E7EB' : 'none' }} data-testid={`link-option-${ev.id}`}>
              <span className="flex-none overflow-hidden" style={{ width: 44, height: 44, borderRadius: 14, background: ev.posterUrl ? '#111' : posterPlaceholder(ev.id || ev.title) }}>
                {ev.posterUrl && <img src={ev.posterUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
              </span>
              <span className="flex min-w-0 flex-col" style={{ flex: '1 1 180px', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{ev.title}</span>
                <span className="uppercase" style={{ fontSize: 11, color: MUTED }}>{[ev.dateTime ? whenLabel(ev.dateTime).replace(',', ' ·') : null, ev.status ? String(ev.status).replace(/_/g, ' ').toUpperCase() : null].filter(Boolean).join(' · ')}</span>
              </span>
              <PillButton size={34} fontSize={9} disabled={busy} onClick={() => onLink(ev.id)} data-testid={`link-confirm-${ev.id}`}>LINK</PillButton>
            </div>
          ))}
        </div>
      )}
      {error && <span style={{ fontSize: 11, color: RED }}>{error}</span>}
    </ClubPanel>
  );
}

export default function ClubEventsTab({
  club, state, events = [], gatherings = [], showHidden, onToggleShowHidden, onHide, onUnlink, onLink, linkBusy, linkError, mobile = false, loadErrors = {}, onRetryLoad }) {
  const navigate = useNavigate();
  const [picker, setPicker] = useState(false);
  const { isArmed, confirm } = useArmed();
  const { upcoming, past } = splitEvents(events);
  const linkedIds = React.useMemo(() => new Set(events.map((e) => e.id)), [events]);
  const gatheringsOn = club.gatheringsEnabled !== false;
  const isAdmin = !!state.isAdmin;
  const locked = !!state.locked;
  const visibleGatherings = gatherings.filter((g) => g.status !== 'draft' || isAdmin);

  const unlink = (e) => { if (confirm(`unlink-${e.id}`)) onUnlink(e); };
  const rowProps = (e, i, second) => ({ event: e, admin: isAdmin, armedUnlink: isArmed(`unlink-${e.id}`), onHide, onUnlink: unlink, phone: mobile, ctaFill: !second });

  const tools = isAdmin ? (
    <span className="flex flex-wrap" style={{ gap: 6 }}>
      <PillButton variant={picker ? 'primary' : 'tertiary'} size={36} fontSize={10} onClick={() => setPicker((v) => !v)} data-testid="link-existing">{picker ? 'CLOSE' : 'LINK EXISTING'}</PillButton>
      <PillButton size={36} fontSize={10} onClick={() => navigate(`/host/events/create?clubId=${club.id}`)} data-testid="create-event">CREATE EVENT</PillButton>
      <PillButton variant={showHidden ? 'cyan' : 'tertiary'} size={36} fontSize={10} onClick={onToggleShowHidden} aria-pressed={showHidden} data-testid="toggle-hidden">{showHidden ? 'HIDE HIDDEN' : 'SHOW HIDDEN'}</PillButton>
    </span>
  ) : null;

  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="club-events">
      {!locked && gatheringsOn && (visibleGatherings.length > 0 || isAdmin) && (
        <ClubPanel phone={mobile} data-testid="events-gatherings">
          <PanelHead title="GATHERINGS" right={isAdmin ? undefined : 'BUNDLED WEEKENDS'}>
            {isAdmin && <PillButton variant="cyan" size={30} fontSize={9} className="hover:bg-black hover:text-[#00FFFF]" onClick={() => navigate(`/clubs/${club.id}/gatherings/new`)} data-testid="new-gathering">+ NEW GATHERING</PillButton>}
          </PanelHead>
          {loadErrors.gatherings ? <LoadFailed what="the gatherings" onRetry={onRetryLoad} testId="gatherings-failed" />
            : visibleGatherings.length === 0 ? <span style={{ fontSize: 12, color: MUTED }}>No gatherings yet — spin up a multi-day agenda of events.</span>
              : (
                <div className="flex flex-col">
                  {visibleGatherings.map((g, i) => {
                    const draft = g.status === 'draft';
                    const [bg, fg] = EVENT_STATUS_PILL[String(g.status || 'draft').toLowerCase()] || EVENT_STATUS_PILL.draft;
                    return (
                      <div key={g.id} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '12px 0', borderTop: i ? RULE : 'none' }} data-testid={`gathering-${g.id}`}>
                        <span className="flex min-w-0 flex-col" style={{ flex: '1 1 180px', gap: 4 }}>
                          <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{g.name}</span>
                            <TagPill bg={draft ? '#F2F2F2' : bg} fg={draft ? '#000' : fg}>{String(g.status || 'draft').toUpperCase()}</TagPill>
                          </span>
                          <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', lineHeight: 1.5, color: MUTED }}>{[g.startsAt ? `${dateLabel(g.startsAt)}${g.endsAt && dateLabel(g.endsAt) !== dateLabel(g.startsAt) ? ` – ${dateLabel(g.endsAt)}` : ''}` : null, plural(g.eventCount || 0, 'EVENT'), g.ticketTotalCents > 0 ? `${money(g.ticketTotalCents)} IN TICKETS` : null, g.isPrivate ? 'PRIVATE' : null].filter(Boolean).join(' · ')}</span>
                          {g.agendaPreview?.length > 0 && <span style={{ fontSize: 11, color: SUBTLE }}>{g.agendaPreview.slice(0, 3).map((e) => `${dayLabel(e.dateTime)} ${e.title}`).join(' · ')}</span>}
                        </span>
                        <PillButton variant={draft ? 'primary' : 'tertiary'} size={34} fontSize={9} onClick={() => navigate(`/clubs/${club.id}/gatherings/${g.id}`)}>{draft ? 'FINISH IT' : 'SEE IT'}</PillButton>
                      </div>
                    );
                  })}
                </div>
              )}
        </ClubPanel>
      )}

      {isAdmin && picker && <LinkPicker linkedIds={linkedIds} busy={linkBusy} error={linkError} phone={mobile} onLink={async (id) => { const ok = await onLink(id); if (ok) setPicker(false); }} />}

      <ClubPanel phone={mobile} data-testid="events-upcoming">
        <PanelHead title={`UPCOMING · ${upcoming.length}`} right={locked ? 'PUBLIC EVENTS ONLY' : undefined} />
        {tools}
        {loadErrors.events ? <LoadFailed what="the events" onRetry={onRetryLoad} testId="events-failed" />
          : upcoming.length === 0
            ? <EmptyBlock title="NOTHING COMING UP" body={isAdmin ? 'Create an event or link one you already host.' : 'No events on the calendar yet.'} action={isAdmin ? 'CREATE EVENT' : null} onAction={() => navigate(`/host/events/create?clubId=${club.id}`)} />
            : <div className="flex flex-col" style={{ gap: 8 }}>{upcoming.map((e, i) => <ClubEventRow key={e.id} {...rowProps(e, i, i > 0)} />)}</div>}
      </ClubPanel>

      {past.length > 0 && (
        <ClubPanel phone={mobile} data-testid="events-past">
          <PanelHead title={`PAST · ${past.length}`} />
          <div className="flex flex-col" style={{ gap: 8 }}>{past.map((e, i) => <ClubEventRow key={e.id} {...rowProps(e, i, true)} cta={false} />)}</div>
        </ClubPanel>
      )}
    </div>
  );
}
